import { useAuth0 } from '@auth0/auth0-react'
import { Alert, Dropdown } from '@designsforhealth/dfh-react-components'
import { TextField as MuiTextField } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import Link from 'gatsby-link'
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useId } from 'react-id-generator'
import styled from 'styled-components'

import { SanityParentProductFragment } from '../../../../graphql/gatsby'
import {
  ConfigurableProductDetailsFragment,
  SimpleProductDetailsFragment,
} from '../../../../graphql/magento'
import { ProductLabelStatus } from '../../../../graphql/search'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import useUserCategory from '../../../../hooks/useUserCategory'
import { useUserAccessPolicy } from '../../../../lib/auth/user-access-policy'
import {
  autoShipIntervalOptions,
  resolveItemQuantityConfig,
  selectOptionsToDisplay,
  validAddQuantityOptionsFor,
} from '../../../../lib/cart/item-control-options'
import { useDispensaryContext } from '../../../../lib/dispensaries'
import { useDispensaryCartStatus } from '../../../../lib/dispensaries/events/cart'
import { useShowAutoShipAndSaveToUserGroup } from '../../../../lib/m2/user-groups'
import { prescriptionBuilderActions } from '../../../../lib/prescriptions/builder/actions'
import { useAddToPrescriptionToast } from '../../../../lib/prescriptions/hooks/useAddToPrescriptionToast'
import { usePrescriptionBuilder } from '../../../../lib/prescriptions/hooks/usePrescriptionBuilder'
import { useTestKitDelayedBilling } from '../../../../lib/products/hooks/useTestKitDelayedBilling'
import { useTestKitProducts } from '../../../../lib/products/hooks/useTestKitProducts'
import {
  AttributeName,
  AttributeValue,
  defaultItemFormValues,
  FormattedVariantData,
  isCombinationNotAvailable,
  maxItemOrderQuantityByUserCategory,
  stockMessageDisplay,
} from '../../../../utils/itemTools'
import { itemDetailsPriceDisplay } from '../../../../utils/priceTools'
import { userCategories } from '../../../../utils/userClaims'
import { DispensaryTestKitRequest } from '../../../dispensaries/test-kit-request/DispensaryTestKitRequest'
import TextField from '../../../form/TextField'
import { AutoShipControlLabel } from '../../../global/cart/AutoShipControlLabel'
import CircularProgressVariant from '../../../global/CircularProgressVariant'
import { PrescriptionIcon } from '../../../prescriptions/PrescriptionIcon'
import { FulfillmentTimeAcknowledgement } from '../FulfillmentTimeAcknowledgement'
import ItemVariantOptions from '../ItemVariantOptions'
import { TestKitLegalTerms } from '../TestKitLegalTerms'
import { TestKitPaymentAgreement } from '../TestKitPaymentAgreement'
import * as Styled from './styled'

export interface ItemControlsProps {
  className?: string
  addToCart: AddToCart | null
  availableVariantOptions: Record<AttributeName, AttributeValue[]> | null
  controls: 'add_to_escript' | 'add_to_cart' | 'all'
  error: ReactElement | null
  onVariantSelected: (itemValue: string) => void
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  submitting: boolean
  variantData: FormattedVariantData
  variantLabelStatus: ProductLabelStatus | undefined
  quantityInCart: number | undefined
  itemUrlKey: string | null | undefined
  labelInformationInCart: { label: string; value: string }[] | undefined
  item: ConfigurableProductDetailsFragment
  sanityParentProduct?: SanityParentProductFragment | null
}

export type AddToCart = (options: AddToCartOptions) => void

export interface AddToCartOptions {
  qty: string
  orderType: string | number
  autoShipInterval: string
  // Label Design Information for PL
  desiredName?: string
  additionalDetails?: string
}

interface FieldValues {
  // nest dynamic fields for variant attributes to avoid index signature on FieldValues
  variant: Record<AttributeName, AttributeValue>

  qty: { label: string; value: string }
  dfh_autoship_interval: { label: string; value: string }
  dfh_order_type: number

  // label information
  desired_name: string
  additional_notes?: string
}

// TODO split this up into separate components?
const ItemControlsReactive: FC<ItemControlsProps> = ({
  addToCart,
  availableVariantOptions = {},
  className,
  controls,
  error,
  onVariantSelected,
  selectedVariant = null,
  submitting,
  variantData,
  variantLabelStatus,
  quantityInCart,
  itemUrlKey,
  labelInformationInCart,
  item,
  sanityParentProduct,
}) => {
  const ctaColor = sanityParentProduct?.primaryColor?.color ?? undefined
  const { featureFlags } = useSiteMetadata() ?? {}
  const { userCategory } = useUserCategory()
  const testKitDelayedBilling = useTestKitDelayedBilling(selectedVariant)
  const showAutoShipAndSaveToUserGroup = useShowAutoShipAndSaveToUserGroup()
  const showAutoShipAndSave = !!featureFlags?.autoShipAndSave && showAutoShipAndSaveToUserGroup
  const { loading: dispensaryCartLoading } = useDispensaryCartStatus()
  const itemQuantityConfig = useMemo(
    () =>
      selectedVariant ? resolveItemQuantityConfig(selectedVariant.stock_configuration) : undefined,
    [selectedVariant],
  )

  // collect set of valid quantities from which select options are produced and custom values validated
  const validAddQuantityOptionValues = useMemo(
    () =>
      itemQuantityConfig
        ? validAddQuantityOptionsFor(itemQuantityConfig)({
            curQuantity: quantityInCart ?? 0,
            maxItemOrderQuantityByUserCategory: maxItemOrderQuantityByUserCategory(userCategory),
          })
        : [],
    [itemQuantityConfig, quantityInCart, userCategory],
  )

  const quantityOptions = useMemo(
    () => selectOptionsToDisplay(validAddQuantityOptionValues),
    [validAddQuantityOptionValues],
  )

  // use `null` instead of `undefined` since the latter does not clear the control
  const defaultQuantityOption = useMemo(() => quantityOptions[0] ?? null, [quantityOptions])

  // track previous default quantity for updating quantity selection intelligently
  const [previousDefaultQuantity, setPreviousDefaultQuantity] = useState(
    defaultQuantityOption?.value,
  )

  const labelInformation = useMemo(
    () =>
      labelInformationInCart?.reduce(
        (acc, value, index) => {
          if (labelInformationInCart && labelInformationInCart.length <= 2) {
            acc[`${index === 0 ? 'labelName' : 'labelDetails'}`] = value.value
          }
          return acc
        },
        {} as { labelName: string; labelDetails: string },
      ),
    [labelInformationInCart],
  )

  // Avoid building on each render. Also leave out conditionally rendered fields so the `defaultValue` prop
  // works as expected.
  const [defaultValues] = useState<Pick<FieldValues, 'variant' | 'qty'>>(() => ({
    variant: defaultItemFormValues({
      ...variantData,
      availableVariantOptions,
      item,
    }),
    qty: defaultQuantityOption,
  }))

  const methods = useForm<FieldValues>({
    defaultValues,
  })

  const { control, handleSubmit, register, watch, setError, setValue, trigger } = methods

  const { isAuthenticated } = useAuth0()
  const dispensary = useDispensaryContext()
  const isPatient = userCategory === userCategories.patient
  const isPractitioner = userCategory === userCategories.practitioner
  const { canPurchaseTestKits } = useUserAccessPolicy()

  const { usedAttributes } = variantData

  const currentAutoshipInterval = watch('dfh_autoship_interval', autoShipIntervalOptions[0])
  const currentOrderType = watch('dfh_order_type', 1)
  const currentQty = watch('qty')
  const variantSelections = watch('variant')

  useEffect(() => {
    const defaultQuantity = defaultQuantityOption?.value
    if (previousDefaultQuantity !== defaultQuantity) {
      // update current quantity to new default if set to previous default quantity
      if (currentQty && currentQty.value === previousDefaultQuantity) {
        setValue('qty', defaultQuantityOption)
      }
      setPreviousDefaultQuantity(defaultQuantity)
    }
  }, [currentQty, defaultQuantityOption, previousDefaultQuantity, setValue])

  // update current qty selection when no longer valid
  useEffect(() => {
    const currentQtyValue = currentQty ? Number(currentQty.value) : undefined
    const isCurrentQtyValid =
      !!currentQtyValue && validAddQuantityOptionValues.includes(currentQtyValue)
    if (!isCurrentQtyValid) {
      setValue('qty', defaultQuantityOption, {
        shouldDirty: false,
        shouldValidate: false,
      })
    }
  }, [currentQty, defaultQuantityOption, setValue, validAddQuantityOptionValues])

  const onSubmit = (values: FieldValues) => {
    // enforce testKitPaymentAgree requirement here instead of in element so trigger()
    // in handlePrescribeToPatient() does not trigger requirement error
    if (watch('testKitPaymentAgree') === false) {
      setError('testKitPaymentAgree', { type: 'required' })
      return
    }
    if (!addToCart) {
      return
    }
    addToCart({
      autoShipInterval: (values.dfh_autoship_interval ?? autoShipIntervalOptions[0]).value,
      orderType: values.dfh_order_type ?? 1,
      qty: values.qty.value,
      ...(values.desired_name && {
        desiredName: values.desired_name,
        additionalDetails: values.additional_notes || undefined,
      }),
    })
  }

  const hasSelectedVariantCombination =
    Object.values(variantSelections).filter((value) => value).length === usedAttributes.length

  const combinationNotAvailable = isCombinationNotAvailable({
    hasSelectedVariantCombination,
    variantSelections,
    productIndex: variantData.productIndex,
  })

  const stockMessage = stockMessageDisplay({
    selectedVariant,
    combinationNotAvailable,
  })

  const priceText = currentQty
    ? itemDetailsPriceDisplay({ selectedVariant, currentQty, ignoreFalsyAmount: true })
    : ''

  const { isTestKitSku } = useTestKitProducts()
  const isTestKit = isTestKitSku(selectedVariant?.sku || '')

  const anonymousCart = featureFlags?.anonymousCart ?? false

  const isVariantLabelStatusAbsent = variantLabelStatus === ProductLabelStatus.Absent

  const allowAddToCart =
    (isAuthenticated || !!dispensary || anonymousCart) &&
    (!isTestKit || (isAuthenticated && canPurchaseTestKits)) &&
    !combinationNotAvailable

  const showDispensaryTestKitRequest = isTestKit && (!isAuthenticated || isPatient)

  const showAddToCartActions =
    (!showDispensaryTestKitRequest && controls === 'all') || controls === 'add_to_cart'

  const isDisabledFormInput =
    !allowAddToCart ||
    !addToCart ||
    submitting ||
    // prevent form input for $0 items except for delayed billing test kits
    (!priceText && !testKitDelayedBilling) ||
    selectedVariant?.stock_status === 'OUT_OF_STOCK'

  const showAutoShip = showAddToCartActions && !isTestKit && !isVariantLabelStatusAbsent

  const showPrescribeToPatient =
    isPractitioner &&
    featureFlags?.manageEScriptBuilder &&
    (controls === 'add_to_escript' || controls === 'all')

  const [addingToPrescription, setAddingToPrescription] = useState(false)

  const { dispatch, prescription } = usePrescriptionBuilder()
  const { showSuccessToast: showPrescribeToPatientToast } = useAddToPrescriptionToast()

  const [singleOrderElementId] = useId(1, 'single-order')
  const [autoShipElementId] = useId(1, 'auto-ship')

  const handlePrescribeToPatient = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    trigger()
      .then((isValid) => {
        if (!isValid) return
        setAddingToPrescription(true)

        // simulate delay for UX
        setTimeout(
          () => {
            if (!selectedVariant?.sku) {
              return
            }
            showPrescribeToPatientToast()
            dispatch(
              prescriptionBuilderActions.add({
                sku: selectedVariant.sku,
                quantity: Number(currentQty.value),
                autoShip: [2, '2'].includes(currentOrderType)
                  ? (Number(currentAutoshipInterval.value) as 1 | 2 | 3)
                  : null,
                urlKey: itemUrlKey ?? '',
              }),
            )
            setAddingToPrescription(false)
          },
          500 + Math.floor(Math.random() * 300),
        )
      })
      .catch(() => {
        // noop
      })
  }

  const quantityItemsInEScript = useMemo(
    () => prescription.products.reduce((acc, { quantity }) => acc + quantity, 0),
    [prescription.products],
  )

  return (
    <FormProvider {...methods}>
      <form className={className} noValidate onSubmit={handleSubmit(onSubmit)}>
        {!testKitDelayedBilling && (
          <ItemVariantOptions
            item={item}
            hasSelectedVariantCombination={hasSelectedVariantCombination}
            register={register}
            variantData={variantData}
            onVariantSelected={onVariantSelected}
            variantSelections={variantSelections}
            variantLabelStatus={variantLabelStatus}
          />
        )}
        {(isVariantLabelStatusAbsent || !!labelInformation) && (
          <>
            <p className="small-paragraph small-margin-bottom label-information-header">
              Label information is required before adding this item to your cart
            </p>
            {!!labelInformation && (
              <Alert type="attention">
                {`To update your label information at this time, you will need to remove the item
                from your cart and re-enter the information on this page.`}
              </Alert>
            )}
            <div className="label-information">
              {labelInformation ? (
                <>
                  <MuiTextField
                    name="desired_name_readonly"
                    label="Desired Name"
                    value={labelInformation?.labelName}
                    helperText={
                      <>
                        <Styled.HelperTextEditIcon />
                        Remove from cart to edit
                      </>
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ disableUnderline: true }}
                    variant="filled"
                    disabled
                    fullWidth
                  />
                  <MuiTextField
                    name="additional_notes_readonly"
                    label="Additional Details (fallback Desired Name, notes)"
                    value={labelInformation?.labelDetails}
                    helperText={
                      <>
                        <Styled.HelperTextEditIcon />
                        Remove from cart to edit
                      </>
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ disableUnderline: true }}
                    variant="filled"
                    fullWidth
                    disabled
                    multiline
                  />
                </>
              ) : (
                <>
                  <TextField
                    name="desired_name"
                    label="Desired Name"
                    disabled={submitting}
                    rules={{ required: true }}
                  />
                  <TextField
                    name="additional_notes"
                    label="Additional Details (fallback Desired Name, notes)"
                    disabled={submitting}
                    multiline
                  />
                </>
              )}
            </div>
          </>
        )}
        {showAutoShip && (
          <div className="item-control">
            <Styled.RadioButtonGroup
              className="order-type"
              defaultValue={1}
              // TODO: check if `id` prop is needed or if types are correct, since TS failed to recognize it
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              id="dfh_order_type"
              name="dfh_order_type"
              disabled={isDisabledFormInput}
              radioButtonOptions={[
                {
                  id: singleOrderElementId,
                  label: 'Single Order',
                  value: 1,
                },
                {
                  id: autoShipElementId,
                  label: (
                    <AutoShipControlLabel
                      showSaveText={showAutoShipAndSave}
                      disabled={isDisabledFormInput}
                    />
                  ),
                  value: 2,
                },
              ]}
              register={register}
            />

            {[1, '1'].includes(currentOrderType) ? (
              <Styled.AutoShipText>
                {showAutoShipAndSave ? (
                  <span>
                    Save 5% on recurrent orders with Auto-Ship.
                    <br />
                    Set up preferences in the next step. Cancel at any time.
                  </span>
                ) : (
                  <span>
                    Set up Auto-Ship and never run out of the supplements you love! Cancel at any
                    time.
                  </span>
                )}
              </Styled.AutoShipText>
            ) : (
              <>
                {showAutoShipAndSave && (
                  <Styled.AutoShipText>
                    <span>You will receive a 5% discount on this product.</span>
                  </Styled.AutoShipText>
                )}
                <Dropdown
                  className="autoship-interval"
                  control={control}
                  defaultValue={autoShipIntervalOptions[0]}
                  id="dfh_autoship_interval"
                  name="dfh_autoship_interval"
                  options={autoShipIntervalOptions}
                  prompt="Ship every:"
                  disabled={isDisabledFormInput}
                />
              </>
            )}
          </div>
        )}
        {isVariantLabelStatusAbsent && (
          <FulfillmentTimeAcknowledgement
            isDisabled={submitting}
            defaultChecked={!!labelInformation?.labelName}
          />
        )}
        {isTestKit && isAuthenticated && canPurchaseTestKits && (
          <TestKitLegalTerms isDisabled={submitting} />
        )}
        {testKitDelayedBilling && <TestKitPaymentAgreement isDisabled={submitting} />}
        <p className="small-paragraph small-margin-bottom stock-message">{stockMessage}</p>
        {showDispensaryTestKitRequest && selectedVariant?.sku && (
          <DispensaryTestKitRequest productSku={selectedVariant.sku} />
        )}
        {showAddToCartActions && (
          <div className="quantity-and-button" data-cy="add-to-cart-button-container">
            <div className="item-control">
              <Styled.DropdownCreatable
                control={control}
                id="qty"
                name="qty"
                options={quantityOptions}
                disabled={dispensaryCartLoading || isDisabledFormInput}
                formatCreateLabel={(inputValue) => inputValue}
                menuPlacement="auto"
                isValidNewOption={(inputValue, _selectValue, selectOptions) => {
                  if (selectOptions.some(({ value }) => value === inputValue)) {
                    return false
                  }
                  const numberValue = Number(inputValue)
                  return (
                    !Number.isNaN(numberValue) && validAddQuantityOptionValues.includes(numberValue)
                  )
                }}
              />
            </div>
            {allowAddToCart ? (
              <Styled.AddToCart
                key="add-to-cart"
                disabled={dispensaryCartLoading || isDisabledFormInput}
                type="submit"
                $ctaColor={ctaColor}
              >
                {submitting || !addToCart ? <CircularProgressVariant /> : 'Add to Cart'}
              </Styled.AddToCart>
            ) : (
              <Styled.DisabledAddToCartButton key="add-to-cart-disabled" type="button">
                Add to Cart
              </Styled.DisabledAddToCartButton>
            )}
          </div>
        )}
        {showPrescribeToPatient && (
          <>
            <div className="quantity-and-button">
              <div
                className="item-control"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Link to="/escript/build">
                  <PrescriptionIcon count={quantityItemsInEScript} />
                </Link>
              </div>
              {allowAddToCart ? (
                <Styled.AddToEScript
                  key="prescribe-to-patient"
                  onClick={handlePrescribeToPatient}
                  disabled={dispensaryCartLoading || isDisabledFormInput || addingToPrescription}
                >
                  {addingToPrescription ? <CircularProgressVariant /> : 'Add to eScript'}
                </Styled.AddToEScript>
              ) : (
                <Styled.DisabledPrescribeButton key="prescribe-to-patient-disabled">
                  {dispensaryCartLoading ? <CircularProgressVariant /> : 'Add to eScript'}
                </Styled.DisabledPrescribeButton>
              )}
            </div>
            <p className="small-paragraph small-margin-bottom">
              {prescription.products.length > 0 ? (
                <>
                  {`${quantityItemsInEScript} item${
                    quantityItemsInEScript > 1 ? 's' : ''
                  } in your eScript `}
                  (<Styled.StyledLink to="/escript/build">click to edit</Styled.StyledLink>)
                </>
              ) : (
                'eScript allows you to send your patients a link with all they need.'
              )}
            </p>
          </>
        )}
        {error && <Styled.ErrorMessage variant="body2">{error}</Styled.ErrorMessage>}
        <Styled.ShakableLoginToSeePrice isProductPage toggle={false} />
      </form>
    </FormProvider>
  )
}

const ItemControls: React.FC<ItemControlsProps> = (props) => (
  <Subscribe>
    <ItemControlsReactive {...props} />
  </Subscribe>
)

const StyledItemControls = styled(ItemControls)(({ theme }) => ({
  marginBottom: '16px',
  borderBottom: '1px solid rgba(0,0,0,0.12)',
  '.autoship-interval': {
    width: '175px',
  },
  '.control-header': {
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'bold',
    letterSpacing: '0.06em',
    lineHeight: '14px',
    marginBottom: '8px',
    textTransform: 'uppercase',
  },
  '.item-control': {
    marginBottom: '20px',
  },
  '.label-information': {
    display: 'flex',
    flexDirection: 'column',
  },
  '.label-information-header': {
    color: theme.colors.grayscale.black,
    fontWeight: 'bold',
  },
  '.order-type': {
    marginBottom: '8px',
  },
  '.quantity-and-button': {
    display: 'flex',
    marginBottom: '24px',
    button: {
      flex: 1,
    },
    '.item-control': {
      flex: '0 0 110px',
      marginBottom: '0',
      marginRight: '8px',
    },
  },
  '.stock-message': {
    color: theme.colors.semantic.error,
  },
  'prescribe-to-patient': {
    display: 'flex',
    marginBottom: '20px',
    button: {
      flex: 1,
    },
    '.item-control': {
      flex: '0 0 110px',
      marginBottom: '0',
      marginRight: '8px',
    },
  },
}))

export default StyledItemControls
